import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styles from './ChatHistory.module.css';
import { MdMoreVert, MdDelete, MdEdit, MdCheck, MdClose } from 'react-icons/md';
import { deleteConversation, updateConversationTitle } from "../../api/chatApi";
import { ConversationsContext } from '../../context/ConversationsContext';
import AlertBox from '../alert-box/AlertBox';

const ChatHistory = ({ conversations, searchQuery = '' }) => {
    const { conversationId } = useParams();
    const [activeBackground, setActiveBackground] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const { refreshConversations } = useContext(ConversationsContext);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const renameInputRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        setActiveBackground(conversationId);
    }, [conversationId]);

    useEffect(() => {
        if (editingId && renameInputRef.current) {
            renameInputRef.current.focus();
            renameInputRef.current.select();
        }
    }, [editingId]);

    useEffect(() => {
        if (!activeDropdown) return;
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeDropdown]);

    const handleDelete = async (conversationID) => {
        try {
            await deleteConversation(conversationID);
            refreshConversations();
            if (conversationID === conversationId) {
                window.location.href = '/';
            }
        } catch (error) {
            console.error('Error deleting conversation:', error);
            setError('Failed to delete conversation. Please try again.');
        }
    };

    const handleMenuClick = (e, conversationID) => {
        e.stopPropagation();
        setActiveDropdown(activeDropdown === conversationID ? null : conversationID);
    };

    const handleRenameClick = (conversation) => {
        setEditingId(conversation.conversationID);
        setEditingTitle(conversation.title);
        setActiveDropdown(null);
    };

    const handleRenameSubmit = async (conversationID) => {
        const trimmed = editingTitle.trim();
        if (!trimmed) {
            setEditingId(null);
            return;
        }
        try {
            await updateConversationTitle(conversationID, trimmed);
            await refreshConversations();
        } catch (err) {
            console.error('Error renaming conversation:', err);
            setError('Failed to rename conversation. Please try again.');
        } finally {
            setEditingId(null);
        }
    };

    const handleRenameKeyDown = (e, conversationID) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleRenameSubmit(conversationID);
        } else if (e.key === 'Escape') {
            setEditingId(null);
        }
    };

    const highlightMatch = (text, query) => {
        if (!query || !text) return text;
        const idx = text.toLowerCase().indexOf(query.toLowerCase());
        if (idx === -1) return text;
        return (
            <>
                {text.slice(0, idx)}
                <mark className={styles['search-highlight']}>{text.slice(idx, idx + query.length)}</mark>
                {text.slice(idx + query.length)}
            </>
        );
    };

    return (
        <div className={styles['chat-history']}>
            <AlertBox message={error} onDismiss={() => setError(null)} />
            {[...conversations].map((conversation) => (
                <div 
                    key={conversation.conversationID} 
                    className={`${styles['chat-history-item']} ${activeBackground === conversation.conversationID ? styles['active'] : ''}`} 
                >
                    {editingId === conversation.conversationID ? (
                        <div className={styles['rename-container']}>
                            <input
                                ref={renameInputRef}
                                className={styles['rename-input']}
                                type="text"
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onKeyDown={(e) => handleRenameKeyDown(e, conversation.conversationID)}
                                maxLength={255}
                            />
                            <div className={styles['rename-actions']}>
                                <button
                                    className={styles['rename-confirm']}
                                    onClick={() => handleRenameSubmit(conversation.conversationID)}
                                    aria-label="Confirm rename"
                                >
                                    <MdCheck size={16} />
                                </button>
                                <button
                                    className={styles['rename-cancel']}
                                    onClick={() => setEditingId(null)}
                                    aria-label="Cancel rename"
                                >
                                    <MdClose size={16} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <a href={'/c/' + conversation.conversationID} title={conversation.title}>
                                <div className={styles['chat-history-title']}>
                                    {searchQuery ? highlightMatch(conversation.title, searchQuery) : conversation.title}
                                </div>
                                {searchQuery && conversation.matchSnippet && (
                                    <div className={styles['chat-history-snippet']}>
                                        {highlightMatch(conversation.matchSnippet, searchQuery)}
                                    </div>
                                )}
                            </a>
                            <div className={styles['menu-button']} onClick={(e) => handleMenuClick(e, conversation.conversationID)}>
                                <MdMoreVert size={20} />  
                            </div>
                            {activeDropdown === conversation.conversationID && (
                                <div ref={dropdownRef} className={styles['dropdown-menu']}>
                                    <div className={`${styles['dropdown-item']} ${styles['dropdown-item-rename']}`} onClick={() => handleRenameClick(conversation)}>
                                        <MdEdit size={16} /> <span>Rename</span>
                                    </div>
                                    <div className={`${styles['dropdown-item']} ${styles['dropdown-item-delete']}`} onClick={() => handleDelete(conversation.conversationID)}>
                                        <MdDelete size={16} /> <span>Delete</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ))}
        </div>
    );
};

export default ChatHistory;
